import { Button, HStack } from '@montel/montelpro-shared-components-v2';
import { SUBSCRIPTIONS_API_ROUTE } from 'apiRoutes';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { ConditionallyDisabled } from 'components/common/ConditionallyDisabled/ConditionallyDisabled';
import { AdminRole } from 'domain/account/enums';
import {
	Subscription,
	SubscriptionStatus,
	SubscriptionType,
} from 'domain/subscription/interfaces/Subscription';
import useSubmit, { POST } from 'hooks/useSubmit';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { RequestStatus } from 'types/RequestStatus';
import { subscriptionCanBeEdited } from 'utils/subscriptionCanBeEdited';
import { CancelSubscriptionModal } from './CancelSubscriptionModal/CancelSubscriptionModal';
import { ReallocateSubscriptionModal } from './ReallocateSubscriptionModal';
import { DeleteSubscriptionModal } from './DeleteSubscriptionModal/DeleteSubscriptionModal';
import { EditSubscriptionTooltipContent } from './EditSubscriptionTooltipContent';
import useAuthRoles from 'hooks/useAuthRoles';

export const SubscriptionActions: React.FC<{
	subscription: Subscription;
	onSubscriptionUpdated: () => void;
}> = ({ subscription, onSubscriptionUpdated }) => {
	const [showReallocateModal, setShowReallocateModal] = useState(false);
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const { adminIsRestrictedToRole: isRestrictedToRole } = useAuthRoles();

	const [activateSubscription, activateSubscriptionStatus] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/ActivateSubscription`,
		POST,
		{
			messageOnSuccess: 'Subscription was successfully activated',
			messageOnError: 'Error activating subscription',
			showApiError: true,
		},
	);

	const [reactivateSubscription, reactivateSubscriptionStatus] = useSubmit(
		`${SUBSCRIPTIONS_API_ROUTE}/ReactivateSubscription`,
		POST,
		{
			messageOnSuccess: 'Subscription was successfully reactivated',
			messageOnError: 'Error reactivating subscription',
			showApiError: true,
		},
	);

	const onActivateSubscription = async () => {
		const result = await activateSubscription({
			SubscriptionId: subscription.id,
		});

		if (result.success) {
			onSubscriptionUpdated();
		}
	};

	const onReactivateSubscription = async () => {
		const result = await reactivateSubscription({
			SubscriptionId: subscription.id,
		});

		if (result.success) {
			onSubscriptionUpdated();
		}
	};

	const canBeCancelled =
		subscription?.status === SubscriptionStatus.Active ||
		subscription?.status === SubscriptionStatus.Ordered;

	const canBeActivated = subscription?.status === SubscriptionStatus.Ordered;

	const canBeReallocated =
		subscription?.seats == 1 &&
		subscription?.occupiedSeats === 1 &&
		subscription?.status === SubscriptionStatus.Active;

	const canBeReactivated =
		subscription?.status === SubscriptionStatus.Canceled;

	const canBeDeleted = subscription?.occupiedSeats === 0;

	const isSalesAdmin = isRestrictedToRole(AdminRole.SALES);
	const canEditSubscription = subscriptionCanBeEdited(
		subscription?.status,
		subscription?.type,
		isSalesAdmin,
	);

	const navigate = useNavigate();

	return (
		<>
			<HStack gap="md" align="center">
				<ConditionallyDisabled
					disableIf={!canEditSubscription}
					tooltip={
						<EditSubscriptionTooltipContent
							subscriptionStatus={subscription?.status}
							subscriptionType={subscription?.type}
							isSalesAdmin={isSalesAdmin}
						/>
					}
				>
					<Button
						variant="secondary"
						text="Edit subscription"
						size="md"
						onClick={() => {
							navigate(`/subscriptions/${subscription.id}/edit`);
						}}
					/>
				</ConditionallyDisabled>
				{canBeActivated && (
					<Button
						variant="primary"
						text="Activate Subscription"
						size="md"
						onClick={onActivateSubscription}
						isDisabled={isRestrictedToRole(AdminRole.SALES)}
						isLoading={
							activateSubscriptionStatus === RequestStatus.LOADING
						}
					/>
				)}
				{canBeReactivated && (
					<ConditionallyDisabled
						disableIf={isSalesAdmin}
						tooltip="You do not have permission to reactivate this subscription."
					>
						<Button
							variant="primary"
							text="Reactivate Subscription"
							size="md"
							onClick={onReactivateSubscription}
							isLoading={
								reactivateSubscriptionStatus ===
								RequestStatus.LOADING
							}
						/>
					</ConditionallyDisabled>
				)}
				{canBeReallocated && (
					<ConditionallyDisabled
						disableIf={isSalesAdmin}
						tooltip="You do not have permission to reallocate a subscription."
					>
						<Button
							variant="secondary"
							text="Reallocate Subscription"
							size="md"
							onClick={() => setShowReallocateModal(true)}
							isLoading={
								activateSubscriptionStatus ===
								RequestStatus.LOADING
							}
						/>
					</ConditionallyDisabled>
				)}
				{canBeCancelled && (
					<ConditionallyDisabled
						disableIf={
							isSalesAdmin &&
							subscription.type !== SubscriptionType.Trial
						}
						tooltip="You only have permission to cancel trial subscriptions."
					>
						<Button
							variant="negative"
							text="Cancel Subscription"
							size="md"
							onClick={() => setShowCancelModal(true)}
						/>
					</ConditionallyDisabled>
				)}
				{canBeDeleted && (
					<AccessControl allowedRoles={[AdminRole.SUPER_ADMIN]}>
						<Button
							variant="negative"
							text="Delete Subscription"
							size="md"
							onClick={() => setShowDeleteModal(true)}
						/>
					</AccessControl>
				)}
			</HStack>

			<CancelSubscriptionModal
				setShowModal={setShowCancelModal}
				showModal={showCancelModal}
				subscriptionId={subscription.id}
				onSubscriptionCancelled={onSubscriptionUpdated}
			/>
			<ReallocateSubscriptionModal
				isOpen={showReallocateModal}
				onClose={() => setShowReallocateModal(false)}
				onSuccess={(targetSubscriptionId: number) => {
					setShowReallocateModal(false);
					navigate(`/subscriptions/${targetSubscriptionId}`);
				}}
				originalSubscription={subscription}
			/>
			<DeleteSubscriptionModal
				setShowModal={setShowDeleteModal}
				showModal={showDeleteModal}
				subscriptionId={subscription.id}
				onSubscriptionDeleted={onSubscriptionUpdated}
			/>
		</>
	);
};
